import { useRef, useState } from 'react';
import { siteConfig } from './siteConfig';
import './App.css';

const assets = `${process.env.PUBLIC_URL}/assets`;
const worlds = [
  { id: 'forest', name: 'Forest', title: 'Where curiosity takes root.', subtitle: 'Mossy paths. Hidden rivals. A little wild possibility.', width: 1448, height: 1086, alt: 'Four small hooded challengers face off among moss-covered rocks, dense foliage, and fallen timber in a forest sanctuary.' },
  { id: 'desert', name: 'Desert', title: 'Nothing stays buried forever.', subtitle: 'Warm sandstone. Winding lanes. Trouble around the corner.', width: 1448, height: 1086, alt: 'Four colorful challengers surround a Rune Charge in a sunlit desert arena of sandstone, dry scrub, and weathered timber.' },
  { id: 'snow', name: 'Snow', title: 'A chill in the air. A spark of mischief.', subtitle: 'Frosted pines. Quiet trails. A very lively rivalry.', width: 1672, height: 941, alt: 'Four young challengers meet in a snowy sanctuary with granite rocks, frosted brush, and pine trees.' },
];

function Arrow({ diagonal = false, down = false, ...props }) {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}><path d={down ? 'M12 4v15m-6-6 6 6 6-6' : diagonal ? 'M5 19 19 5M5 5h14v14' : 'M4 12h16m-6-6 6 6-6 6'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function Spark({ className = '' }) {
  return <svg className={className} width="32" height="32" viewBox="0 0 40 40" fill="none" aria-hidden="true"><path d="M20 1 24.7 15.3 39 20 24.7 24.7 20 39 15.3 24.7 1 20 15.3 15.3Z" fill="currentColor" /></svg>;
}

function Logo({ footer = false }) {
  return <a className={`studio-logo${footer ? ' studio-logo--footer' : ''}`} href="#top" aria-label="VoxelVortex home"><img src={`${assets}/voxel-vortex-on-dark.svg`} width="2056" height="765" alt="VoxelVortex" /></a>;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef(null);
  function followSection(event, id) {
    setMenuOpen(false);
    if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      document.getElementById(id)?.focus({ preventScroll: true });
    }
  }
  return (
    <header className="site-header wrap" onKeyDown={event => {
      if (event.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    }}>
      <Logo />
      <button ref={menuButton} className={`menu-toggle${menuOpen ? ' is-open' : ''}`} type="button" aria-label={menuOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={menuOpen} aria-controls="main-navigation" onClick={() => setMenuOpen(!menuOpen)}><span /><span /></button>
      <nav id="main-navigation" className={`main-navigation${menuOpen ? ' is-open' : ''}`} aria-label="Main navigation">
        <a href="#game" onClick={event => followSection(event, 'game')}>Our game</a>
        <a href="#worlds" onClick={event => followSection(event, 'worlds')}>Inside the world</a>
        <a href="#studio" onClick={event => followSection(event, 'studio')}>The studio</a>
        <a className="nav-contact" href="#contact" onClick={event => followSection(event, 'contact')}>Let’s talk <Arrow diagonal /></a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero-scene" aria-hidden="true"><img src={`${assets}/forest-1448.webp`} srcSet={`${assets}/forest-768.webp 768w, ${assets}/forest-1448.webp 1448w`} sizes="(max-width: 700px) 115vw, 95vw" width="1448" height="1086" fetchPriority="high" alt="" /></div>
      <div className="hero-content wrap">
        <div className="hero-copy">
          <p className="eyebrow hero-eyebrow"><span className="small-spark">✳</span> Independently made. Endlessly curious.</p>
          <h1 id="hero-title">Small worlds.<br /><em>Big wonder.</em></h1>
          <p className="hero-description">A little mystery. A little mischief.<br />We make games that bring out your<br className="desktop-break" /> curious, playful, one-more-round side.</p>
          <a className="button button--cream" href="#game">Meet Hidden Hunters <span className="button-arrow"><Arrow /></span></a>
        </div>
        <div className="hero-bottom">
          <a className="scroll-link" href="#game"><span className="circle-arrow"><Arrow down /></span><span>Good things await<br /><strong>Keep exploring</strong></span></a>
          <a className="hero-game-note" href="#game"><img src={`${assets}/beacon-768.webp`} width="56" height="56" alt="" /><span><span className="eyebrow">Our first adventure</span><strong>Hidden Hunters <Arrow diagonal /></strong></span></a>
        </div>
      </div>
      <span className="hero-art-note">Hidden Hunters · Forest concept art</span>
    </section>
  );
}

function WorldGallery() {
  const [active, setActive] = useState(0);
  const tabs = useRef([]);
  function navigateTabs(event, index) {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % worlds.length;
    if (event.key === 'ArrowLeft') next = (index + worlds.length - 1) % worlds.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = worlds.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      setActive(next);
      tabs.current[next]?.focus();
    }
  }
  return (
    <section id="worlds" className="worlds-section" tabIndex="-1" aria-labelledby="worlds-title">
      <div className="worlds-heading"><div><p className="eyebrow">A peek beyond the Veil</p><h3 id="worlds-title">Let your curiosity <em>wander.</em></h3></div><p>Different landscapes.<br />The same playful possibilities.</p></div>
      <div className="world-gallery">
        {worlds.map((world, index) => (
          <div key={world.id} id={`world-${world.id}`} role="tabpanel" aria-labelledby={`tab-${world.id}`} tabIndex="0" hidden={active !== index} className={`world-panel world-panel--${world.id}`}>
            <img src={`${assets}/${world.id}-${world.width}.webp`} srcSet={`${assets}/${world.id}-768.webp 768w, ${assets}/${world.id}-${world.width}.webp ${world.width}w`} sizes="(max-width: 700px) calc(100vw - 40px), (max-width: 1500px) calc(100vw - 112px), 1388px" width={world.width} height={world.height} loading="lazy" alt={world.alt} />
            <span className="world-stamp"><span /> Worlds in the making</span>
            <div className="world-caption"><span className="world-number">0{index + 1} / 03</span><h4>{world.title}</h4><p>{world.subtitle}</p></div>
          </div>
        ))}
        <div className="world-tabs" role="tablist" aria-label="Explore world concepts">
          {worlds.map((world, index) => <button key={world.id} ref={node => { tabs.current[index] = node; }} role="tab" id={`tab-${world.id}`} aria-controls={`world-${world.id}`} aria-selected={active === index} tabIndex={active === index ? 0 : -1} onClick={() => setActive(index)} onKeyDown={event => navigateTabs(event, index)}><span>0{index + 1}</span>{world.name}<Arrow diagonal /></button>)}
        </div>
      </div>
      <p className="art-caption"><span>From the Hidden Hunters sketchbook</span><span>Environment concepts · The world is still taking shape</span></p>
    </section>
  );
}

function Game() {
  return (
    <section id="game" className="game-section paper" tabIndex="-1" aria-labelledby="game-title">
      <div className="wrap">
        <div className="section-heading"><p className="eyebrow"><span className="section-number">01 /</span> Our first adventure</p><span className="development-status"><span /> In development</span></div>
        <div className="game-intro">
          <div className="game-identity"><h2 id="game-title"><span>Hidden</span>Hunters</h2><div className="game-meta"><span>Mobile</span><span>Multiplayer</span><span>A little magical</span></div></div>
          <div className="game-description"><h3>Out of sight.<br /><em>Full of possibility.</em></h3><p>Step into the Veiled Trial, a magical arena where rivals are hidden, curiosity is rewarded, and a clever move can change everything.</p><a className="text-link" href="#trial">Get to know the game <Arrow down /></a></div>
        </div>
        <WorldGallery />
        <div id="trial" className="trial-section">
          <div className="trial-intro"><p className="eyebrow">Small steps. Clever moves.</p><h3>A little mystery.<br /><em>A lot to play for.</em></h3><p>Explore the unknown. Stir up a little trouble.<br />Make your moment count.</p></div>
          <div className="trial-details">
            <details name="trial" open><summary><span className="trial-number">01</span><span>Discover what’s hidden.</span><span className="details-plus" aria-hidden="true" /></summary><p>The Veil keeps your rivals just beyond sight. Find your way, break open cover with Rune Charges, and collect the Light Shards hidden inside.</p></details>
            <details name="trial"><summary><span className="trial-number">02</span><span>Make a little mischief.</span><span className="details-plus" aria-hidden="true" /></summary><p>Place a Rune Charge. Set off a chain reaction. Turn a narrow escape into your next clever move. Just remember: your own bursts can catch you, too.</p></details>
            <details name="trial"><summary><span className="trial-number">03</span><span>Become the Keeper.</span><span className="details-plus" aria-hidden="true" /></summary><p>As Shards are discovered, the Beacon awakens. Reach it and hold your claim without interruption to become Keeper of the Beacon. Being the last challenger standing is only the beginning.</p></details>
          </div>
        </div>
        <div className="game-footnote"><span className="small-spark">✳</span><p>Something good is in the making.<br /><span>In development for iOS & Android phones and tablets.</span></p><a className="text-link" href="#contact">Stay in touch <Arrow diagonal /></a></div>
      </div>
    </section>
  );
}

function Studio() {
  return (
    <section id="studio" className="studio-section" tabIndex="-1" aria-labelledby="studio-title">
      <div className="wrap">
        <div className="section-heading"><p className="eyebrow"><span className="section-number">02 /</span> The people behind the play</p><Spark /></div>
        <div className="studio-intro">
          <div><h2 id="studio-title">Serious about<br /><em>being playful.</em></h2><p className="studio-lead">We’re VoxelVortex. An independent game studio<br className="desktop-break" /> following a simple instinct: <span>what if?</span></p><p className="studio-body">What if the smallest world held the biggest surprise? What if a rival made the adventure better? We’re bringing those questions to life, one thoughtful detail at a time. Hidden Hunters is our first answer.</p></div>
          <figure className="studio-beacon"><div className="beacon-orbit" aria-hidden="true"><Spark /><span /></div><img src={`${assets}/beacon-768.webp`} width="768" height="768" loading="lazy" alt="The Hidden Hunters Beacon: a warm golden flame held in a sculpted stone vessel against teal mist." /><figcaption>Every world starts with a little spark.</figcaption></figure>
        </div>
        <div className="studio-values">
          <article><span className="eyebrow">01 — The feeling</span><h3>Wonder comes first.</h3><p>Worlds that invite a second look, reward curiosity, and leave room for imagination.</p></article>
          <article><span className="eyebrow">02 — The connection</span><h3>Better with a little rivalry.</h3><p>The close calls, clever surprises, and shared stories that make you want another round.</p></article>
          <article><span className="eyebrow">03 — The craft</span><h3>Small details. Big heart.</h3><p>Care in every corner. From how a world feels to the moment everything clicks.</p></article>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="contact-section" tabIndex="-1" aria-labelledby="contact-title">
      <div className="wrap contact-grid">
        <div><p className="eyebrow"><span className="section-number">03 /</span> Good things start with hello</p><h2 id="contact-title">Got a little<br /><em>spark?</em><Spark /></h2></div>
        <div className="contact-copy"><p>A question about the game, a collaboration,<br className="desktop-break" /> or just a shared love of play.<br />We’d love to hear from you.</p><a className="contact-link" href={`mailto:${siteConfig.contactEmail}`}><span>{siteConfig.contactEmail}</span><span className="contact-arrow"><Arrow diagonal /></span></a><span className="contact-note">Straight to the people making the game.</span></div>
      </div>
    </section>
  );
}

function App() {
  return (
    <div className="site" id="top">
      <a className="skip-link" href="#main">Skip to content</a>
      <Header />
      <main id="main" tabIndex="-1">
        <Hero />
        <div className="play-strip" aria-label="A little curiosity. A little mischief. A lot of play."><span>A little curiosity</span><Spark /><span>A little mischief</span><Spark /><span>A lot of play</span><Spark /></div>
        <Game />
        <Studio />
        <Contact />
      </main>
      <footer className="site-footer wrap"><div className="footer-top"><Logo footer /><p>Independent minds.<br />Shared adventures.</p><a className="back-top" href="#top" aria-label="Back to top"><Arrow down /></a></div><div className="footer-bottom"><span>© {new Date().getFullYear()} VoxelVortex</span><span>Made with curiosity. Played with a grin.</span><span>Keep a little wonder.</span></div></footer>
    </div>
  );
}

export default App;
